import React from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { 
  Star, 
  MessageSquare,
  Calendar,
  BarChart3,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Phone,
  PlusCircle,
  Globe,
  ExternalLink,
  Search,
  Filter
} from 'lucide-react'
import { SyncStatusWidget } from '@/components/gmb/shared/SyncStatusWidget'

interface OutletContext {
  locations: any[]
  selectedLocation: string | null
  setSelectedLocation: (id: string) => void
  selectedLocationData: any
}

export default function GMBOverview() {
  const { locations, selectedLocation, setSelectedLocation, selectedLocationData } = useOutletContext<OutletContext>()
  
  console.log('GMBOverview - Locations:', locations)
  console.log('GMBOverview - Selected Location:', selectedLocation)
  console.log('GMBOverview - Selected Location Data:', selectedLocationData)
  
  return (
    <div className="space-y-4">
      {/* Debug Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
        <p className="font-semibold mb-2">Debug Info:</p>
        <p>Locations count: {locations?.length || 0}</p>
        <p>Selected: {selectedLocation || 'none'}</p>
        <p>Has selected data: {selectedLocationData ? 'yes' : 'no'}</p>
      </div>
      
      {/* Sync Status Widget */}
      <SyncStatusWidget 
        detailed={true}
        enableQuickSync={false}
      />

      {/* Location Selector */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Locations</CardTitle>
              <CardDescription>Select a location to manage</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Search className="mr-2 h-4 w-4" />
                Search
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {locations?.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {locations.map((location) => (
              <div
                key={location.id}
                className={`relative rounded-lg border p-4 cursor-pointer transition-colors hover:bg-accent ${
                  selectedLocation === location.id ? 'border-primary bg-accent' : ''
                }`}
                onClick={() => setSelectedLocation(location.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-sm">
                      {location.name || location.account_name || 'Unnamed Location'}
                    </h4>
                    <p className="text-xs text-muted-foreground">{location.address}</p>
                  </div>
                  {location.place_id && (
                    <Badge variant="secondary" className="text-xs">Verified</Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {location.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span>Phone</span>
                    </div>
                  )}
                  {location.website && (
                    <div className="flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      <span>Website</span>
                    </div>
                  )}
                </div>
                {selectedLocation === location.id && (
                  <div className="absolute top-2 right-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                  </div>
                )}
              </div>
            ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No locations found</p>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/integrations/gmb/sync'}
              >
                Sync Locations
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedLocationData && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Location Details */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedLocationData.name || selectedLocationData.account_name || 'Unnamed Location'}
                </CardTitle>
                <CardDescription>Location Information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium mb-1">Address</div>
                  <p className="text-sm text-muted-foreground">{selectedLocationData.address}</p>
                </div>
                {selectedLocationData.phone && (
                  <div>
                    <div className="text-sm font-medium mb-1">Phone</div>
                    <p className="text-sm text-muted-foreground">{selectedLocationData.phone}</p>
                  </div>
                )}
                {selectedLocationData.website && (
                  <div>
                    <div className="text-sm font-medium mb-1">Website</div>
                    <a 
                      href={selectedLocationData.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      Visit Website
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
                <div className="pt-4">
                  <Button className="w-full">
                    Edit Location Info
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks for this location</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Respond to Reviews
                  <Badge variant="destructive" className="ml-auto">3 new</Badge>
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Create New Post
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Calendar className="mr-2 h-4 w-4" />
                  Update Business Hours
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  View Performance Report
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest updates for this location</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-blue-100 p-2">
                    <Star className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">New 5-star review</p>
                    <p className="text-xs text-muted-foreground">Sarah M. left a glowing review 2 hours ago</p>
                  </div>
                  <Button variant="ghost" size="sm">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-green-100 p-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Post published</p>
                    <p className="text-xs text-muted-foreground">"Spring Promotion" post went live yesterday</p>
                  </div>
                  <Button variant="ghost" size="sm">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-orange-100 p-2">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Question needs answer</p>
                    <p className="text-xs text-muted-foreground">Customer asked about service availability</p>
                  </div>
                  <Button variant="ghost" size="sm">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}